import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, Loader2, Calendar, LayoutGrid, RefreshCw, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import CalendarWeekView from './components/CalendarWeekView';
import CalendarMonthView from './components/CalendarMonthView';
import PostComposer from './PostComposer';
import { getPosts, getIntegrations, deletePost, publishPost } from '../../api/socialMedia';

const PostCalendar = () => {
    // State
    const [viewMode, setViewMode] = useState('month'); // 'week' | 'month'
    const [currentDate, setCurrentDate] = useState(dayjs());
    const [posts, setPosts] = useState([]);
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Composer state
    const [isComposerOpen, setIsComposerOpen] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [initialDate, setInitialDate] = useState(null);

    // Confirm delete state
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Calculate date range for fetching posts
    const getDateRange = useCallback(() => {
        if (viewMode === 'week') {
            const start = currentDate.startOf('week');
            const end = currentDate.endOf('week');
            return { start: start.toISOString(), end: end.toISOString() };
        } else {
            const start = currentDate.startOf('month').startOf('week');
            const end = currentDate.endOf('month').endOf('week');
            return { start: start.toISOString(), end: end.toISOString() };
        }
    }, [currentDate, viewMode]);

    // Load data
    const loadData = useCallback(async () => {
        try {
            setError(null);
            setLoading(true);

            const { start, end } = getDateRange();

            const [postsRes, integrationsRes] = await Promise.all([
                getPosts({ start_date: start, end_date: end, limit: 100 }),
                getIntegrations()
            ]);

            setPosts(postsRes.data || []);
            setIntegrations(integrationsRes.data || []);
        } catch (err) {
            console.error('Failed to load calendar data:', err);
            setError(err.message || 'Failed to load calendar data');
        } finally {
            setLoading(false);
        }
    }, [getDateRange]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Navigation
    const navigatePrev = () => {
        if (viewMode === 'week') {
            setCurrentDate(prev => prev.subtract(1, 'week'));
        } else {
            setCurrentDate(prev => prev.subtract(1, 'month'));
        }
    };

    const navigateNext = () => {
        if (viewMode === 'week') {
            setCurrentDate(prev => prev.add(1, 'week'));
        } else {
            setCurrentDate(prev => prev.add(1, 'month'));
        }
    };

    const goToToday = () => {
        setCurrentDate(dayjs());
    };

    // Handle slot click (create new post)
    const handleSlotClick = (dateTime) => {
        setEditingPost(null);
        setInitialDate(dateTime);
        setIsComposerOpen(true);
    };

    // Handle post click (edit)
    const handlePostClick = (post) => {
        setEditingPost(post);
        setInitialDate(null);
        setIsComposerOpen(true);
    };

    // Handle delete
    const handleDelete = async (post) => {
        try {
            setIsDeleting(true);
            await deletePost(post.id);
            setPosts(prev => prev.filter(p => p.id !== post.id));
            setConfirmDelete(null);
        } catch (err) {
            console.error('Failed to delete post:', err);
            setError(err.message || 'Failed to delete post');
        } finally {
            setIsDeleting(false);
        }
    };

    // Handle publish
    const handlePublish = async (post) => {
        try {
            await publishPost(post.id);
            loadData(); // Reload to get updated status
        } catch (err) {
            console.error('Failed to publish post:', err);
            setError(err.message || 'Failed to publish post');
        }
    };

    // Get display title
    const getTitle = () => {
        if (viewMode === 'week') {
            const weekStart = currentDate.startOf('week');
            const weekEnd = currentDate.endOf('week');
            if (weekStart.month() === weekEnd.month()) {
                return weekStart.format('MMMM YYYY');
            }
            return `${weekStart.format('MMM')} - ${weekEnd.format('MMM YYYY')}`;
        }
        return currentDate.format('MMMM YYYY');
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    {/* Navigation */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={navigatePrev}
                            className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={navigateNext}
                            className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Current date */}
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                        {getTitle()}
                    </h2>

                    <button
                        onClick={goToToday}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        Today
                    </button>
                </div>

                <div className="flex items-center gap-3">
                    {/* View toggle */}
                    <div className="flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
                        <button
                            onClick={() => setViewMode('week')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                viewMode === 'week'
                                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <Calendar className="w-4 h-4" />
                            Week
                        </button>
                        <button
                            onClick={() => setViewMode('month')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                viewMode === 'month'
                                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <LayoutGrid className="w-4 h-4" />
                            Month
                        </button>
                    </div>

                    {/* Refresh */}
                    <button
                        onClick={loadData}
                        disabled={loading}
                        className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>

                    {/* Create post */}
                    <button
                        onClick={() => {
                            setEditingPost(null);
                            setInitialDate(null);
                            setIsComposerOpen(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Create Post
                    </button>
                </div>
            </div>

            {/* Error display */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="px-6 py-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 flex items-start gap-3"
                    >
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-700 dark:text-red-400 flex-1">{error}</p>
                        <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                            ×
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Calendar view */}
            <div className="flex-1 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
                    </div>
                ) : viewMode === 'week' ? (
                    <CalendarWeekView
                        currentDate={currentDate}
                        posts={posts}
                        integrations={integrations}
                        onSlotClick={handleSlotClick}
                        onPostClick={handlePostClick}
                        onPostEdit={handlePostClick}
                        onPostDelete={(post) => setConfirmDelete(post)}
                        onPostPublish={handlePublish}
                    />
                ) : (
                    <CalendarMonthView
                        currentDate={currentDate}
                        posts={posts}
                        integrations={integrations}
                        onSlotClick={handleSlotClick}
                        onPostClick={handlePostClick}
                        onPostEdit={handlePostClick}
                        onPostDelete={(post) => setConfirmDelete(post)}
                        onPostPublish={handlePublish}
                    />
                )}
            </div>

            {/* Post Composer Modal */}
            <PostComposer
                isOpen={isComposerOpen}
                onClose={() => {
                    setIsComposerOpen(false);
                    setEditingPost(null);
                    setInitialDate(null);
                }}
                editingPost={editingPost}
                initialDate={initialDate}
                onSave={loadData}
            />

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {confirmDelete && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
                        onClick={() => setConfirmDelete(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-md p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-xl"
                        >
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                Delete Post?
                            </h3>
                            <p className="text-slate-600 dark:text-slate-400 mb-6">
                                Are you sure you want to delete this post? This action cannot be undone.
                            </p>
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={() => setConfirmDelete(null)}
                                    className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDelete(confirmDelete)}
                                    disabled={isDeleting}
                                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2"
                                >
                                    {isDeleting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PostCalendar;
